/**
 * `RenamePinDialog` — small shadcn Dialog + input for renaming a
 * `pinned-workspace` entry (Task 16). Owns no store access itself — the
 * caller (`DatabaseExplorer`) supplies the initial name and receives the
 * trimmed new name via `onSave`; the store write + analytics track happen
 * at the call site so this component stays a pure, reusable prompt.
 */

import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

type RenamePinDialogProps = {
  open: boolean;
  initialName: string;
  onSave: (name: string) => void;
  onCancel: () => void;
};

export function RenamePinDialog({ open, initialName, onSave, onCancel }: RenamePinDialogProps) {
  const [name, setName] = useState(initialName);

  // Reset the input whenever the dialog opens for a (possibly different) pin.
  useEffect(() => {
    if (open) setName(initialName);
  }, [open, initialName]);

  const trimmed = name.trim();
  const canSave = trimmed.length > 0;

  const handleSave = () => {
    if (!canSave) return;
    onSave(trimmed);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onCancel(); }}>
      <DialogContent className="max-w-sm" data-testid="rename-pin-dialog">
        <DialogHeader>
          <DialogTitle>Rename Pin</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="rename-pin-input">Name</Label>
          <Input
            id="rename-pin-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave();
            }}
            data-testid="rename-pin-input"
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="rename-pin-cancel">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSave} data-testid="rename-pin-save">
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
